import axios from "axios";
import { Vehicle } from "../../../state/types";
import { MediaFile } from "./UploadDialog";

/**
 * Interface for the request to update image ordering in DynamoDB
 */
interface UpdateImageOrderRequest {
  pk: string;
  sk?: string;  // Optional sort key in case it can't be derived from pk
  imageType: string;
  imageArray: any[];
}

/**
 * Interface for the request to update image tag in DynamoDB
 */
interface UpdateImageTagRequest {
  pk: string;
  sk?: string;  // Optional sort key in case it can't be derived from pk
  imageType: string;
  imageIndex: number;
  newTag: string;
}

/**
 * Interface for the request to delete an image from DynamoDB
 */
interface DeleteImageRequest {
  pk: string;
  sk?: string;  // Optional sort key in case it can't be derived from pk
  imageType: string;
  imageIndex: number;
}

/**
 * Uploads a single image to S3 using presigned URL
 */
const uploadImage = async (vehicle: Vehicle, image: MediaFile, uploadIdx: number) => {
  // Convert image type from UI format to backend format
  const imageTypeMap: Record<string, string> = {
    'imageExterior': 'exterior_raw',
    'imageManual': 'manual_raw',
    'imagePano': 'pano_raw',
    'imageFeatured': 'featured',
    'imageMask': 'masking',
    'imageCrop': 'cropping',
    'imageUndercarriage': 'undercarriage_raw'
  };
  
  // Get the appropriate backend image type
  const targetType = imageTypeMap[image.mediaGroup] || 'exterior_raw';
  
  // Create a meaningful filename based on assetIdentifier and sequential number
  const fileName = `${vehicle.assetIdentifier}-${String(uploadIdx + 1).padStart(2, '0')}.jpg`;
  
  const requestBody = {
    clientName: vehicle.clientIdentifier,
    pk: vehicle.assetId || vehicle.assetId,
    fileName: fileName,
    target: targetType
  };

  const headers = {
    Authorization: 'bwenvdev',
    'Content-Type': 'application/json',
  };

  try {
    // Step 1: Get presigned URL
    const presignedResponse = await axios.put(
      `${process.env.REACT_APP_UMS_API_BASE_URL}/asset/upload`, 
      requestBody, 
      { headers }
    );
    
    const presignedUrl = presignedResponse.data.presignedUrl;

    // Step 2: Upload file to S3 using presigned URL
    await axios.put(presignedUrl, image.file, {
      headers: {
        'Content-Type': 'image/jpeg'
      }
    });
    
    return {
      success: true,
      fileName,
      imageGroup: image.mediaGroup
    };
  } catch(error) {
    console.error("Upload error:", error);
    return {
      success: false,
      error
    };
  }
};

/**
 * Updates the image order in DynamoDB
 */
const updateImageOrder = async (vehicle: Vehicle, imageType: string, imageArray: any[]) => {
  // Extract the sort key from the vehicle asset ID
  const assetIdParts = (vehicle.assetId || vehicle.assetId).split('-');
  let sk = '';
  
  if (assetIdParts.length > 1) {
    sk = assetIdParts[1];
  }
  
  const requestBody: UpdateImageOrderRequest = {
    pk: vehicle.assetId || vehicle.assetId,
    sk: sk,  // Include the sort key explicitly
    imageType,
    imageArray
  };

  const headers = {
    Authorization: 'bwenvdev',
    'Content-Type': 'application/json',
  };

  try {
    // Configure axios for CORS
    const response = await axios.put(
      `${process.env.REACT_APP_UMS_API_BASE_URL}/asset/updateImageOrder`, 
      requestBody, 
      { 
        headers,
        // Add withCredentials if you're using cookies for authorization
        // withCredentials: true
      }
    );
    
    return {
      success: true,
      data: response.data
    };
  } catch(error) {
    console.error("Error updating image order:", error);
    return {
      success: false,
      error
    };
  }
};

/**
 * Updates an individual image tag in DynamoDB
 */
const updateImageTag = async (vehicle: Vehicle, imageType: string, imageIndex: number, newTag: string) => {
  // Extract the sort key from the vehicle asset ID
  const assetIdParts = (vehicle.assetId || vehicle.assetId).split('-');
  let sk = '';
  
  if (assetIdParts.length > 1) {
    sk = assetIdParts[1];
  }
  
  const requestBody: UpdateImageTagRequest = {
    pk: vehicle.assetId || vehicle.assetId,
    sk: sk,  // Include the sort key explicitly
    imageType,
    imageIndex,
    newTag
  };

  const headers = {
    Authorization: 'bwenvdev',
    'Content-Type': 'application/json',
  };

  try {
    const response = await axios.put(
      `${process.env.REACT_APP_UMS_API_BASE_URL}/asset/updateImageTag`, 
      requestBody, 
      { headers }
    );
    
    return {
      success: true,
      data: response.data
    };
  } catch(error) {
    console.error("Error updating image tag:", error);
    return {
      success: false,
      error
    };
  }
};

/**
 * Deletes an image from DynamoDB array
 */
const deleteImage = async (vehicle: Vehicle, imageType: string, imageIndex: number) => {
  // Extract the sort key from the vehicle asset ID
  const assetIdParts = (vehicle.assetId || vehicle.assetId).split('-');
  let sk = '';
  
  if (assetIdParts.length > 1) {
    sk = assetIdParts[1];
  }
  
  const requestBody: DeleteImageRequest = {
    pk: vehicle.assetId || vehicle.assetId,
    sk: sk,  // Include the sort key explicitly
    imageType,
    imageIndex
  };

  const headers = {
    Authorization: 'bwenvdev',
    'Content-Type': 'application/json',
  };

  try {
    const response = await axios.delete(
      `${process.env.REACT_APP_UMS_API_BASE_URL}/asset/deleteImage`, 
      { 
        headers,
        data: requestBody  // DELETE requests need data in the config object
      }
    );
    
    return {
      success: true,
      data: response.data
    };
  } catch(error) {
    console.error("Error deleting image:", error);
    return {
      success: false,
      error
    };
  }
};

/**
 * Comprehensive function to handle both uploading new images and updating order
 */
const uploadAndProcessImages = async (vehicle: Vehicle, uploadedFiles: MediaFile[], orderedImageTypes: Record<string, any[]>) => {
  // Step 1: Upload all new files
  const uploadPromises = uploadedFiles.map((item, idx) => 
    uploadImage(vehicle, item, idx)
  );
  
  const uploadResults = await Promise.all(uploadPromises);
  
  // Step 2: Update the order of any reordered image types
  const orderUpdatePromises = Object.entries(orderedImageTypes).map(([imageType, imageArray]) => 
    updateImageOrder(vehicle, imageType, imageArray)
  );
  
  const orderResults = await Promise.all(orderUpdatePromises);
  
  // Step 3: Return combined results
  return {
    uploads: uploadResults,
    orderUpdates: orderResults
  };
};

export { uploadImage, updateImageOrder, updateImageTag, deleteImage, uploadAndProcessImages };
export default uploadAndProcessImages;